import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { MenuItemModel } from '../../common/models';
import { IconNames, StatusLabelColor } from '../../common/constants';
import { RouterFacade } from '../../store/state/router/router.facade';
import { ProjectionModule } from '../../container-components/projection/projection.module';
import { ProjectorName } from '../../container-components/projection/projector.constant';
import { AuthFacade } from '../../store/state/auth';
import { IntrackFeatureModule } from '../../intrack-feature.module';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { from, Observable, of } from 'rxjs';
import { filter, map, switchMap, toArray } from 'rxjs/operators';

@Component({
  selector: 'ui-page',
  imports: [
    IntrackFeatureModule,
    RouterModule,
    MatTabsModule,
    MatButtonModule,
    MatProgressBarModule,
    ProjectionModule,
    TranslateModule,
    SvgIconComponent,
  ],
  templateUrl: './page.component.html',
  styleUrls: ['./page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageComponent {
  @Input() title: string;
  @Input() subtitle: string;
  @Input() hideBackBtn: boolean;
  @Input() backBtnPath: string;
  @Input() backBtnQueryParams: { [key: string]: unknown };
  @Input() labelText: string;
  @Input() labelColor: StatusLabelColor = StatusLabelColor.Red;
  @Input() preserveQueryParams = false;
  @Input() tooltipText: string;
  @Input() noIndents = false;

  @Input() set menuList(menu: MenuItemModel[]) {
    if (!menu) {
      return;
    }
    this.displayedMenuItems$ = this.filteredMenuByPermissions(menu);
  }

  readonly routerFacade = inject(RouterFacade);
  private readonly authFacade = inject(AuthFacade);

  projectorName = ProjectorName;
  displayedMenuItems$: Observable<MenuItemModel[]>;
  iconNameList = IconNames;

  private filteredMenuByPermissions(allMenuItems: MenuItemModel[]): Observable<MenuItemModel[]> {
    return from(allMenuItems).pipe(
      switchMap((item: MenuItemModel) =>
        item.permission
          ? this.authFacade.hasPermission$(item.permission).pipe(map((hasPermission: boolean) => [item, hasPermission]))
          : of([item, true]),
      ),
      filter(([_item, hasPermission]: [_item: MenuItemModel, hasPermission: boolean]) => hasPermission),
      map(([item, _hasPermission]: [item: MenuItemModel, _hasPermission: boolean]) => item),
      toArray(),
    );
  }
}
